import type { ConditionAnalysisSlice } from "../../../utils/conditionAnalysisSelectors";
import {
  regimeDrilldownTableKeyFromBreakdownMap,
  regimeDrilldownTableTitle,
} from "../../../utils/marketContextDrilldownLabels";
import {
  formatDirectionBarsWindow,
  marketCandleDirectionLabel,
} from "../../../utils/marketRegimeDirectionLookbacks";

export function getSliceCategoryLabel(
  kind: string,
  candleTimeframe?: string | null
): string {
  const long = marketCandleDirectionLabel("long", candleTimeframe);
  const short = marketCandleDirectionLabel("short", candleTimeframe);
  switch (kind) {
    case "all":
      return "Aggregate";
    case "regime":
      return "Regime";
    case "direction":
      return "Trade direction";
    case "candle_direction":
    case "market_direction":
      return long;
    case "candle_direction_short":
    case "market_direction_short":
      return short;
    case "regime_direction":
      return `Regime + ${long}`;
    case "candle_direction_trade_direction":
      return `${long} + trade direction`;
    case "candle_direction_short_trade_direction":
      return `${short} + trade direction`;
    case "regime_direction_trade_direction":
      return `Regime + ${long} + trade direction`;
    case "session_time":
      return "Trading session";
    case "session_trade_direction":
      return "Session + trade side";
    case "session_candle_direction":
      return `Trading session + ${long}`;
    case "session_candle_direction_short":
      return `Trading session + ${short}`;
    case "session_regime":
      return "Session + regime";
    case "session_regime_trade_direction":
      return "Session + regime + trade side";
    case "session_regime_direction_trade_direction":
      return `Trading session + regime + ${long} + trade direction`;
    case "session_regime_direction_short_trade_direction":
      return `Trading session + regime + ${short} + trade direction`;
    default:
      return kind.replace(/_/g, " ");
  }
}

/** Compact badge copy for narrow Category columns — full label stays in tooltip. */
export function getSliceCategoryShortLabel(
  kind: string,
  candleTimeframe?: string | null
): string {
  if (kind === "all") return "Aggregate";
  if (kind === "session_time") return "Session";
  const tableKey = sliceKindToDrilldownTableKey(kind);
  if (!tableKey) {
    const full = getSliceCategoryLabel(kind, candleTimeframe);
    return full.length <= 28 ? full : "Combined";
  }
  return getExplorerCategoryShortLabelForTableKey(tableKey, candleTimeframe);
}

export function getSliceCategoryBadgeGradient(slice: ConditionAnalysisSlice): string {
  switch (slice.kind) {
    case "all":
      return "linear-gradient(135deg, #38BBD1, #3EEAEC)";
    case "regime":
      return "linear-gradient(135deg, #8B5CF6, #C084FC)";
    case "direction":
      if (slice.direction === "long") return "linear-gradient(135deg, #06E363, #AAFAB1)";
      if (slice.direction === "short") return "linear-gradient(135deg, #E00101, #FE4244)";
      return "linear-gradient(135deg, #6B7280, #9CA3AF)";
    case "market_direction":
    case "market_direction_short":
    case "candle_direction":
    case "candle_direction_short":
      return "linear-gradient(135deg,#ffbb01,#f5de0c)";
    case "session_time":
      return "linear-gradient(135deg, #F45B0B, #F49113)";
    case "session_trade_direction":
    case "session_candle_direction":
    case "session_candle_direction_short":
    case "session_regime":
    case "session_regime_trade_direction":
    case "session_regime_direction_trade_direction":
    case "session_regime_direction_short_trade_direction":
      return "linear-gradient(135deg, #0EA5E9, #38BDF8)";
    default:
      return "linear-gradient(135deg, #6B7280, #9CA3AF)";
  }
}

/** Compact slice path for cohort preview labels (no bar-window suffixes). */
export function getCohortPreviewSlicePathLabel(slice: ConditionAnalysisSlice | null | undefined): string | null {
  if (!slice || slice.kind === "all") return null;
  const raw = (slice.label ?? "").trim();
  const cleaned = raw
    .replace(/\s*\(long window\)/gi, "")
    .replace(/\s*\(short dir\)/gi, "")
    .replace(/\s*\(~\d+\s*bars\)/gi, "")
    .trim();
  if (!cleaned) return null;
  return cleaned.replace(/\s*\|\s*/g, " + ").replace(/\s+/g, " ").trim() || null;
}

/** Join active slice path with a row target for preview summary / active button state. */
export function buildCohortPreviewDisplayLabel(
  slicePath: string | null | undefined,
  rowLabel: string,
): string {
  const row = String(rowLabel ?? "").trim();
  const path = String(slicePath ?? "").trim();
  if (!path || path.toLowerCase() === "all" || path.toLowerCase() === "all trades") {
    return row || "Preview";
  }
  const normPath = path.replace(/\s*\|\s*/g, " + ");
  if (!row) return normPath;
  return `${normPath} + ${row}`;
}

export function getDisplaySliceName(
  slice: ConditionAnalysisSlice,
  candleTimeframe?: string | null
): string {
  const raw = (slice.label ?? "").trim();
  const cleaned = raw
    .replace(/\s*\(long window\)/gi, "")
    .replace(/\s*\(short dir\)/gi, "")
    .trim();

  if (/\(~\d+\s*bars\)/i.test(cleaned)) return cleaned;

  const longWin = formatDirectionBarsWindow("long", candleTimeframe);
  const shortWin = formatDirectionBarsWindow("short", candleTimeframe);

  if (slice.kind === "market_direction" || slice.kind === "candle_direction") {
    return cleaned
      ? `${cleaned} (${longWin})`
      : marketCandleDirectionLabel("long", candleTimeframe);
  }

  if (slice.kind === "market_direction_short" || slice.kind === "candle_direction_short") {
    return cleaned
      ? `${cleaned} (${shortWin})`
      : marketCandleDirectionLabel("short", candleTimeframe);
  }

  return cleaned || slice.label || "";
}

/** Map pivot `slice.kind` → regime drilldown table key (for category tooltips). */
export function sliceKindToDrilldownTableKey(kind: string): string {
  switch (kind) {
    case "all":
      return "";
    case "regime":
      return "market_condition";
    case "direction":
      return "trade_direction";
    case "candle_direction":
    case "market_direction":
      return "candle_direction";
    case "candle_direction_short":
    case "market_direction_short":
      return "candle_direction_short";
    case "regime_direction":
      return "market_condition_and_direction";
    case "candle_direction_trade_direction":
      return "candle_direction_and_trade_direction";
    case "candle_direction_short_trade_direction":
      return "candle_direction_short_and_trade_direction";
    case "regime_direction_trade_direction":
      return "market_condition_candle_direction_and_trade_direction";
    case "session_time":
      return "session";
    case "session_trade_direction":
      return "session_and_trade_direction";
    case "session_candle_direction":
      return "session_and_candle_direction";
    case "session_candle_direction_short":
      return "session_and_candle_direction_short";
    case "session_regime":
      return "session_and_market_condition";
    case "session_regime_trade_direction":
      return "session_and_market_condition_and_trade_direction";
    case "session_regime_direction_trade_direction":
      return "session_and_market_condition_and_direction_and_trade_direction";
    case "session_regime_direction_short_trade_direction":
      return "session_and_market_condition_and_direction_short_and_trade_direction";
    default:
      return "";
  }
}

/** Map explorer `breakdown_map` → pivot `slice.kind` for Category badge labels. */
export function breakdownMapToSliceKind(breakdownMap: string): ConditionAnalysisSlice["kind"] {
  const tableKey = regimeDrilldownTableKeyFromBreakdownMap(breakdownMap);
  switch (tableKey) {
    case "market_condition":
      return "regime";
    case "trade_direction":
      return "direction";
    case "candle_direction":
      return "candle_direction";
    case "candle_direction_short":
      return "candle_direction_short";
    case "market_condition_and_direction":
      return "regime_direction";
    case "candle_direction_and_trade_direction":
      return "candle_direction_trade_direction";
    case "candle_direction_short_and_trade_direction":
      return "candle_direction_short_trade_direction";
    case "market_condition_candle_direction_and_trade_direction":
      return "regime_direction_trade_direction";
    case "market_condition_candle_direction_short_and_trade_direction":
      return "regime_direction_trade_direction";
    case "session":
      return "session_time";
    case "session_and_trade_direction":
      return "session_trade_direction";
    case "session_and_candle_direction":
      return "session_candle_direction";
    case "session_and_candle_direction_short":
      return "session_candle_direction_short";
    case "session_and_market_condition":
      return "session_regime";
    case "session_and_market_condition_and_trade_direction":
      return "session_regime_trade_direction";
    case "session_and_market_condition_and_direction_and_trade_direction":
      return "session_regime_direction_trade_direction";
    case "session_and_market_condition_and_direction_short_and_trade_direction":
      return "session_regime_direction_short_trade_direction";
    default:
      return "regime";
  }
}

export function getExplorerCategoryLabel(
  breakdownMap: string,
  candleTimeframe?: string | null
): string {
  const tableKey = regimeDrilldownTableKeyFromBreakdownMap(breakdownMap);
  if (tableKey === "market_condition_candle_direction_short_and_trade_direction") {
    return `Regime + ${marketCandleDirectionLabel("short", candleTimeframe)} + trade direction`;
  }
  return getSliceCategoryLabel(breakdownMapToSliceKind(breakdownMap), candleTimeframe);
}

/** Compact badge label for explorer tables — full label stays in tooltip. */
export function getExplorerCategoryShortLabelForTableKey(
  tableKey: string,
  candleTimeframe?: string | null
): string {
  const longWin = formatDirectionBarsWindow("long", candleTimeframe);
  const shortWin = formatDirectionBarsWindow("short", candleTimeframe);
  switch (tableKey) {
    case "market_condition":
      return "Regime";
    case "trade_direction":
      return "Trade direction";
    case "candle_direction":
      return `Long drift (${longWin})`;
    case "candle_direction_short":
      return `Recent drift (${shortWin})`;
    case "session":
      return "Session";
    case "market_condition_and_direction":
      return "Regime + long drift";
    case "candle_direction_and_trade_direction":
      return "Long drift + trade";
    case "candle_direction_short_and_trade_direction":
      return "Recent drift + trade";
    case "market_condition_candle_direction_and_trade_direction":
      return "Regime + long drift + trade";
    case "market_condition_candle_direction_short_and_trade_direction":
      return "Regime + recent drift + trade";
    case "session_and_trade_direction":
      return "Session + trade";
    case "session_and_candle_direction":
      return "Session + long drift";
    case "session_and_candle_direction_short":
      return "Session + recent drift";
    case "session_and_market_condition":
      return "Session + regime";
    case "session_and_market_condition_and_trade_direction":
      return "Session + regime + trade";
    case "session_and_market_condition_and_direction_and_trade_direction":
      return "Session + regime + long drift + trade";
    case "session_and_market_condition_and_direction_short_and_trade_direction":
      return "Session + regime + recent drift + trade";
    default: {
      const full = regimeDrilldownTableTitle(tableKey, candleTimeframe);
      return full.length <= 32 ? full : "Combined";
    }
  }
}

/** Tab label for explorer category filters (same copy as badge short labels). */
export function getExplorerCategoryTabLabel(
  tableKey: string,
  candleTimeframe?: string | null
): string {
  return getExplorerCategoryShortLabelForTableKey(tableKey, candleTimeframe);
}

/** Compact badge label for explorer tables — full label stays in tooltip. */
export function getExplorerCategoryShortLabel(
  breakdownMap: string,
  candleTimeframe?: string | null
): string {
  const tableKey = regimeDrilldownTableKeyFromBreakdownMap(breakdownMap);
  if (!tableKey) return "Combined";
  return getExplorerCategoryShortLabelForTableKey(tableKey, candleTimeframe);
}

export function getExplorerCategoryGradient(breakdownMap: string): string {
  return getSliceCategoryBadgeGradient({
    kind: breakdownMapToSliceKind(breakdownMap),
  } as ConditionAnalysisSlice);
}
